/**
 * THE CHAT BOX (ADR-0021 §9 part 2; owner, 2026-10-10: "look at how Ramp is").
 *
 * One field. Say the work — typed or spoken — and attach the files. Nothing is
 * chosen from a list before speaking: Leveri reads who and when from the
 * words (the server's /api/chat/understand, the same rule the browser shows
 * as you type), and what it cannot read it ASKS, in the conversation, with
 * one-tap answers — the client, the period, the currency, and whether files
 * continue work already waiting for them.
 *
 * A model reads the words first when one is configured (the server keeps
 * only what it can check); a question is answered from the firm's own work,
 * and something Leveri does not do is said plainly, with what it can do.
 *
 * Before new work starts, Leveri shows its PLAN — the skill's own steps, the
 * records it read, the line it will start on, what it will not conclude — and
 * waits for "Approve and start" (the Ramp standard: propose, then approve).
 *
 * Then it works in the open, as a thread: your words on the right; on the
 * left, what Leveri is doing, step by step ("Opening the work for Northwind
 * Trading · March 2026", "Reading march-statement.pdf — a bank statement"),
 * and it opens the work when it has started it.
 *
 * Every answer is an ordinary act on the server (the client, the engagement
 * and the work are created by their own functions); the thread is how they
 * read, never a second way of doing them.
 *
 * A box on a client's page or an engagement's page is scoped to it, and asks
 * nothing the page already says. ONE BOX, FOUR PLACES (owner ruling
 * 2026-10-12): inside a piece of work it knows the job — words are answered
 * from the job's own file, and files go to that job, answering its open request
 * when there is exactly one. Above the box, each person sees their own recent
 * conversation in that place, and only their own: questions are private, the
 * work is shared.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowUp, Mic, Plus, X } from "lucide-react";
import {
  ChatFeedbackResponse, ChatHistoryResponse, ChatUnderstandResponse, WorkActionResponse, WorkFileResponse, WorkResponse, periodFromWords,
  type ChatQuestion, type ChatUnderstanding, type ControlCandidate, type PeriodFromWords, type WorkFile, type WorkSummary,
} from "@leveri/contracts";
import { FileButton, Marker, Notice } from "../file/index.js";
import { call, encloseAll, failureMessage } from "../api.js";
import { DICTATION_PRIVACY, useDictation } from "./dictation.js";
import { ACCEPT_RECORDS } from "./work/enclose.js";

const READABLE = ACCEPT_RECORDS.split(",").map((e) => e.trim().toLowerCase());
const isReadable = (f: File) => READABLE.some((e) => f.name.toLowerCase().endsWith(e));
const sameFile = (a: File, b: File) => a.name === b.name && a.size === b.size;
const todayIso = () => new Date().toISOString().slice(0, 10);

type Step = { text: string; state: "doing" | "done" | "failed" };
type Answers = { capability?: string; clientId?: string; newClientName?: string; period?: PeriodFromWords; currency?: string; continueWorkId?: string | null };

/** A question the answers so far have not settled. */
const unanswered = (q: ChatQuestion, a: Answers) =>
  (q.key === "work" && !a.capability) || (q.key === "client" && !a.clientId && !a.newClientName) || (q.key === "period" && !a.period) || (q.key === "currency" && !a.currency);
type Asked = { question: string; answer: string };
/** One message sent. Its key ties every re-reading, answer and verdict to it in the learning record (0037). */
interface Turn { instruction: string; files: File[]; key: string }
/** The plan awaiting approval: the opened work, its file as the server projects it, and the start line it would use. */
interface PlanState { work: WorkSummary; file: WorkFile; pick: ControlCandidate | null }

export interface ChatScope { clientId?: string; engagementId?: string; workId?: string }

/** The place's own query: none is Home. */
const placeQuery = (scope?: ChatScope) =>
  scope?.workId ? `?workId=${scope.workId}` : scope?.engagementId ? `?engagementId=${scope.engagementId}` : scope?.clientId ? `?clientId=${scope.clientId}` : "";

export function ChatBox({ scope, placeholder, onOpened, onWork }: {
  scope?: ChatScope;
  placeholder?: string;
  onOpened?: (work: WorkSummary) => void;
  /** Inside a piece of work: the work as it stands after files were sent to it, for the page to show. */
  onWork?: (file: WorkFile) => void;
}) {
  const navigate = useNavigate();
  const [text, setText] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [held, setHeld] = useState<File[]>([]);
  const [turn, setTurn] = useState<Turn | null>(null);
  const [understanding, setUnderstanding] = useState<ChatUnderstanding | null>(null);
  const [answers, setAnswers] = useState<Answers>({});
  const [asked, setAsked] = useState<Asked[]>([]);
  const [steps, setSteps] = useState<Step[]>([]);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [plan, setPlan] = useState<PlanState | null>(null);
  const [basis, setBasis] = useState<"ACCRUALS" | "CASH" | null>(null);
  const area = useRef<HTMLTextAreaElement | null>(null);
  const [earlier, setEarlier] = useState<ChatHistoryResponse["turns"]>([]);

  const speak = useCallback((words: string) => setText((t) => (t.trim() ? `${t.replace(/\s+$/, "")} ${words}` : words)), []);
  const dictation = useDictation(speak);

  // WHAT LEVERI READS AS YOU TYPE: the same rule the server uses, shown before sending.
  // Depends on the scope's ids, never the object: a scope literal is a new object every render.
  const scopeClientId = scope?.clientId; const scopeEngagementId = scope?.engagementId; const scopeWorkId = scope?.workId;
  useEffect(() => {
    if (turn || !text.trim()) { setPreview(null); return; }
    const timer = setTimeout(() => {
      call("POST", "/api/chat/understand", ChatUnderstandResponse, {
        instruction: text, preview: true, ...(scopeClientId ? { clientId: scopeClientId } : {}), ...(scopeEngagementId ? { engagementId: scopeEngagementId } : {}),
        ...(scopeWorkId ? { workId: scopeWorkId } : {}),
      })
        .then((r) => setPreview(r.understanding.summary || null))
        .catch(() => setPreview(null));
    }, 450);
    return () => clearTimeout(timer);
  }, [text, turn, scopeClientId, scopeEngagementId, scopeWorkId]);

  // THE PERSON'S OWN RECENT CONVERSATION in this place (0042): read when the box appears, and again after each message.
  const loadEarlier = useCallback(() => {
    call("GET", `/api/chat/history${placeQuery({ ...(scopeClientId ? { clientId: scopeClientId } : {}), ...(scopeEngagementId ? { engagementId: scopeEngagementId } : {}), ...(scopeWorkId ? { workId: scopeWorkId } : {}) })}`, ChatHistoryResponse)
      .then((r) => setEarlier(r.turns))
      .catch(() => setEarlier([]));
  }, [scopeClientId, scopeEngagementId, scopeWorkId]);
  useEffect(() => { loadEarlier(); }, [loadEarlier]);

  const addFiles = useCallback((list: readonly File[]) => {
    const ok = list.filter(isReadable); const no = list.filter((f) => !isReadable(f));
    if (ok.length) setFiles((h) => [...h, ...ok.filter((f) => !h.some((x) => sameFile(x, f)))]);
    if (no.length) setHeld((h) => [...h, ...no.filter((f) => !h.some((x) => sameFile(x, f)))]);
  }, []);

  /** Work this message may continue, as the server found it: the person's own unfinished work, and work waiting here. */
  const continuable = useMemo(() => understanding?.continuable ?? [], [understanding]);

  /**
   * The questions still open, in the order they are asked. WHETHER TO CONTINUE comes FIRST (found with the first
   * tester, 2026-10-12: each batch of files sent without words started his one job again, five times) — then the
   * server's questions, which a continued work does not need.
   */
  const open = useMemo((): ChatQuestion[] => {
    if (!understanding) return [];
    const withFiles = Boolean(turn && turn.files.length > 0);
    if ((understanding.intent !== "WORK" || understanding.reply) && !withFiles) return [];
    if (continuable.length > 0 && answers.continueWorkId === undefined && (withFiles || understanding.intent === "WORK")) {
      const one = continuable.length === 1 ? continuable[0]! : null;
      return [{
        key: "client" as const, // rendered by its own branch below
        question: withFiles
          ? one ? `Add these files to “${one.title}”?` : "Add these files to work you already have open?"
          : one ? `You already have “${one.title}” open. Continue it?` : "You already have work like this open. Continue one of them?",
        options: [
          ...continuable.map((w) => ({ value: `continue:${w.workId}`, label: withFiles ? `Add to “${w.title}”` : `Continue “${w.title}”`, detail: w.detail })),
          { value: "continue:new", label: "Open new work instead", detail: null },
        ],
        freeText: null,
      }];
    }
    if (understanding.intent !== "WORK" || understanding.reply) return [];
    return understanding.questions.filter((q) => unanswered(q, answers));
  }, [understanding, answers, continuable, turn]);

  const log = (text: string) => setSteps((s) => [...s, { text, state: "doing" }]);
  const settle = (state: Step["state"]) => setSteps((s) => s.map((x, i) => (i === s.length - 1 ? { ...x, state } : x)));

  async function understand(next: Answers, t: Turn) {
    const r = await call("POST", "/api/chat/understand", ChatUnderstandResponse, {
      instruction: t.instruction, ...scope, files: t.files.length, turnKey: t.key,
      ...(next.capability ? { capability: next.capability } : {}),
      ...(next.clientId ? { clientId: next.clientId } : {}),
      ...(next.newClientName ? { newClientName: next.newClientName } : {}),
    });
    setUnderstanding(r.understanding);
    return r.understanding;
  }

  async function send(event?: FormEvent) {
    event?.preventDefault();
    if (busy || (!text.trim() && files.length === 0)) return;
    dictation.stop();
    const t: Turn = { instruction: text.trim(), files, key: crypto.randomUUID() };
    setTurn(t); setText(""); setFiles([]); setHeld([]); setAnswers({}); setAsked([]); setSteps([]); setFailure(null); setPlan(null); setBasis(null);
    setBusy(true);
    try {
      const u = await understand({}, t);
      await proceed(u, {}, t);
    } catch (error) {
      setFailure(failureMessage(error));
    } finally {
      setBusy(false);
      loadEarlier();
    }
  }

  async function answer(q: ChatQuestion, value: string, label: string) {
    if (!turn || !understanding) return;
    setBusy(true); setFailure(null);
    try {
      const next: Answers = { ...answers };
      if (value.startsWith("continue:")) next.continueWorkId = value === "continue:new" ? null : value.slice("continue:".length);
      else if (q.key === "work") next.capability = value;
      else if (q.key === "client") { if (value.startsWith("new:")) next.newClientName = value.slice(4); else next.clientId = value; }
      else if (q.key === "period") next.period = JSON.parse(value) as PeriodFromWords;
      else if (q.key === "currency") next.currency = value;
      setAnswers(next);
      setAsked((a) => [...a, { question: q.question, answer: label }]);
      const u = (q.key === "client" || q.key === "work") && !value.startsWith("continue:") ? await understand(next, turn) : understanding;
      // Continuing a work: the files go to it (or, with no files, the person goes to it); nothing else is asked.
      if (next.continueWorkId) { await continueWork(next.continueWorkId, turn); return; }
      await proceed(u, next, turn);
    } catch (error) {
      setFailure(failureMessage(error));
    } finally {
      setBusy(false);
    }
  }

  /** When nothing is left to ask: open (or continue) the work, read the files, and start it if its start is settled. */
  async function proceed(u: ChatUnderstanding, a: Answers, t: Turn) {
    // Inside a piece of work, files go to that work: nothing is opened and nothing is asked.
    if (scope?.workId) { await sendToThisWork(scope.workId, t); return; }
    // Work this message may continue is offered first; nothing opens until the person says.
    if (u.continuable.length > 0 && a.continueWorkId === undefined && (t.files.length > 0 || (u.intent === "WORK" && !u.reply))) return;
    // A question is answered, and work Leveri does not do is declined, in the thread: nothing opens.
    if (u.intent !== "WORK" || u.reply) return;
    if (u.questions.some((q) => unanswered(q, a))) return;

    let work: WorkSummary;
    {
      log(`Opening the work${u.summary ? ` — ${u.summary}` : ""}`);
      const period = a.period ?? u.period ?? undefined;
      work = (await call("POST", "/api/chat/open", WorkResponse, {
        instruction: t.instruction || "Work from the attached files", turnKey: t.key,
        ...(u.engagement && !a.period ? { engagementId: u.engagement.id } : {}),
        ...(a.clientId ? { clientId: a.clientId } : u.client ? { clientId: u.client.id } : {}),
        ...(a.newClientName ?? (a.clientId ? null : u.newClientName) ? { newClientName: (a.newClientName ?? u.newClientName)! } : {}),
        ...(period ? { period } : {}),
        ...(a.currency ?? u.currency ? { currency: a.currency ?? u.currency! } : {}),
        ...(a.capability ?? u.workKind?.key ? { capability: (a.capability ?? u.workKind?.key)! } : {}),
      })).work;
    }
    settle("done");

    const admitted: string[] = [];
    if (t.files.length > 0) {
      log(`Reading ${t.files.length === 1 ? t.files[0]!.name : `${t.files.length} files`}`);
      const result = await encloseAll(`/api/work/${work.id}/records`, t.files, t.files.map(() => ""));
      admitted.push(...result.records.map((r) => r.record.evidenceReferenceId).filter((x): x is string => Boolean(x)));
      settle(result.undetermined.length > 0 ? "failed" : "done");
      for (const r of result.records) setSteps((s) => [...s, { text: `${r.record.filename} — ${r.record.roleLabel ?? "read"}`, state: "done" }]);
      if (result.undetermined.length > 0) setSteps((s) => [...s, { text: `Leveri could not tell what ${result.undetermined.length === 1 ? "one file is" : `${result.undetermined.length} files are`}; you can say in the work`, state: "failed" }]);
    }

    const file = (await call("GET", `/api/work/${work.id}`, WorkFileResponse)).work;
    // THE PLAN, before anything runs: shown in the thread, started only on approval.
    const pick = file.start.candidates.find((c) => c.proposed) ?? (file.start.candidates.length === 1 ? file.start.candidates[0]! : null);
    setPlan({ work, file, pick: file.start.canStart ? pick : null });
  }

  /** Continue a work the person already has: their files go to it (answering its one open request), then they go to it. */
  async function continueWork(workId: string, t: Turn) {
    const title = continuable.find((w) => w.workId === workId)?.title ?? "the work";
    if (t.files.length > 0) {
      log(`Adding to “${title}”`);
      settle("done");
      await sendToThisWork(workId, t);
    }
    navigate(`/work/${workId}`);
  }

  /** Files sent inside a work: enclosed in it, and the answer to its open request when it has exactly one. */
  async function sendToThisWork(workId: string, t: Turn) {
    if (t.files.length === 0) return;
    log(`Reading ${t.files.length === 1 ? t.files[0]!.name : `${t.files.length} files`}`);
    const result = await encloseAll(`/api/work/${workId}/records`, t.files, t.files.map(() => ""));
    const admitted = result.records.map((r) => r.record.evidenceReferenceId).filter((x): x is string => Boolean(x));
    settle(result.undetermined.length > 0 ? "failed" : "done");
    for (const r of result.records) setSteps((s) => [...s, { text: `${r.record.filename} — ${r.record.roleLabel ?? "read"}`, state: "done" }]);
    if (result.undetermined.length > 0) setSteps((s) => [...s, { text: `Leveri could not tell what ${result.undetermined.length === 1 ? "one file is" : `${result.undetermined.length} files are`}; say below`, state: "failed" }]);
    let file = (await call("GET", `/api/work/${workId}`, WorkFileResponse)).work;
    if (file.openRequests.length === 1 && admitted.length > 0) {
      log(`Answering “${file.openRequests[0]!.questionPlain ?? file.openRequests[0]!.question}”`);
      await call("POST", `/api/work/${workId}/requests/${file.openRequests[0]!.requirementId}`, WorkActionResponse, {
        evidenceReferenceIds: admitted, ...(t.instruction ? { note: t.instruction } : {}), submissionKey: `chat-answer:${workId}:${admitted.join(",").slice(0, 80)}`,
      });
      settle("done");
      file = (await call("GET", `/api/work/${workId}`, WorkFileResponse)).work;
    } else if (file.openRequests.length > 1 && admitted.length > 0) {
      setSteps((s) => [...s, { text: "This work has several open requests: say below which one these files answer.", state: "failed" }]);
    }
    onWork?.(file);
  }

  /** "Approve and start": the one act the plan waits for. */
  async function approve() {
    if (!plan?.pick) return;
    setBusy(true); setFailure(null);
    try {
      log(`Starting — ${plan.pick.label}`);
      await call("POST", `/api/work/${plan.work.id}/start`, WorkActionResponse, {
        control: { candidateId: plan.pick.id }, ...(basis ? { basis } : {}), submissionKey: `chat-start:${plan.work.id}`,
      });
      settle("done");
      onOpened?.(plan.work);
      navigate(`/work/${plan.work.id}`);
    } catch (error) {
      setFailure(failureMessage(error));
    } finally {
      setBusy(false);
    }
  }

  const question = open[0] ?? null;

  const before = earlier.filter((e) => e.turnKey !== turn?.key);

  return (
    <div className="flex flex-col gap-6">
      {before.length > 0 && (
        <details className="group" data-testid="chat-earlier">
          <summary className="cursor-pointer list-none text-[13px] text-stone hover:text-ink">
            <span className="group-open:hidden">Your earlier messages here ({before.length}) ▸</span>
            <span className="hidden group-open:inline">Your earlier messages here ▾</span>
            <span className="ml-2 text-[12px]">Only you see these.</span>
          </summary>
          <ol className="mt-3 space-y-3 border-l border-rule-soft pl-4">
            {before.map((e) => (
              <li key={e.turnKey} className="space-y-0.5">
                <p className="text-[12px] text-stone">{e.atText}</p>
                <p className="text-[14px] text-ink">{e.instruction || "Files sent"}</p>
                {e.openedWork ? (
                  <button type="button" onClick={() => navigate(`/work/${e.openedWork!.id}`)} className="text-left text-[13px] text-forest hover:underline">Opened “{e.openedWork.title}”</button>
                ) : e.reply ? <p className="text-[13px] text-ink-soft">{e.reply}</p> : null}
              </li>
            ))}
          </ol>
        </details>
      )}
      {turn && (
        <div className="flex flex-col gap-5" data-testid="chat-thread">
          <You turn={turn} />
          <div className="flex gap-3">
            <LeveriMark />
            <div className="min-w-0 flex-1 space-y-4">
              {understanding?.intent === "WORK" && understanding.summary && <p className="text-[15px] text-ink">{understanding.summary}</p>}
              {understanding?.reply && <p className="text-[15px] leading-relaxed text-ink" data-testid="chat-reply">{understanding.reply}</p>}
              {understanding?.update && (
                <div className="border border-rule bg-raised" data-testid="chat-update">
                  <button type="button" onClick={() => navigate(`/work/${understanding.update!.workId}`)} className="flex w-full flex-col items-start gap-0.5 border-b border-rule-soft px-5 py-3 text-left transition-colors hover:bg-band">
                    <span className="text-[15px] font-medium text-ink">{understanding.update.title}</span>
                    <span className="text-[12px] text-stone">{understanding.update.detail}</span>
                  </button>
                  <ul className="space-y-1.5 px-5 py-3">
                    {understanding.update.lines.map((line, i) => <li key={i} className="text-[14px] leading-relaxed text-ink-soft">{line}</li>)}
                  </ul>
                </div>
              )}
              {understanding && understanding.answer.length > 0 && (
                <ul className="border border-rule bg-raised" data-testid="chat-answer">
                  {understanding.answer.map((w) => (
                    <li key={w.workId} className="border-t border-rule-soft first:border-t-0">
                      <button type="button" onClick={() => navigate(`/work/${w.workId}`)} className="flex w-full flex-col items-start gap-0.5 px-5 py-3 text-left transition-colors hover:bg-band">
                        <span className="text-[15px] text-ink">{w.title}</span>
                        <span className="text-[12px] text-stone">{w.detail}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              {asked.length > 0 && (
                <dl className="space-y-2 border-l-2 border-rule pl-4">
                  {asked.map((x, i) => (
                    <div key={i}><dt className="text-[13px] text-stone">{x.question}</dt><dd className="text-[15px] text-ink">{x.answer}</dd></div>
                  ))}
                </dl>
              )}
              {question && !busy && <QuestionCard q={question} onAnswer={(v, l) => void answer(question, v, l)} />}
              {steps.length > 0 && <Working steps={steps} />}
              {plan && (
                <PlanCard plan={plan} basis={basis} onBasis={setBasis} busy={busy}
                  onApprove={() => void approve()} onOpen={() => { onOpened?.(plan.work); navigate(`/work/${plan.work.id}`); }} />
              )}
              {busy && steps.length === 0 && !question && <Working steps={[{ text: "Reading what you asked", state: "doing" }]} />}
              {understanding?.reader && <p className="text-[12px] text-stone" data-testid="chat-reader">Read by {understanding.reader.label || understanding.reader.model}, checked by Leveri</p>}
              {turn && understanding && !busy && <NotRight key={turn.key} turnKey={turn.key} />}
              {failure && <Notice>{failure}</Notice>}
            </div>
          </div>
        </div>
      )}

      <form onSubmit={(e) => void send(e)} data-testid="chat-box"
        className="border border-line-strong bg-raised transition-colors focus-within:border-forest focus-within:outline focus-within:outline-1 focus-within:outline-forest">
        {(files.length > 0 || held.length > 0) && (
          <ul className="flex flex-wrap gap-2 px-4 pt-3" aria-label="Files to send">
            {files.map((f) => (
              <li key={`${f.name}-${f.size}`} className="inline-flex max-w-full items-center gap-2 border border-rule-soft bg-canvas py-1 pl-2.5 pr-1 font-mono text-[12px] text-ink">
                <span className="truncate">{f.name}</span>
                <button type="button" aria-label={`Remove ${f.name}`} onClick={() => setFiles((h) => h.filter((x) => !sameFile(x, f)))} className="p-1 text-stone hover:text-ink"><X size={13} /></button>
              </li>
            ))}
            {held.map((f) => (
              <li key={`held-${f.name}`} className="inline-flex items-center gap-2 py-1 text-[12px] text-att-text">
                {f.name}: Leveri reads spreadsheets and PDF statements today
                <button type="button" aria-label={`Remove ${f.name}`} onClick={() => setHeld((h) => h.filter((x) => !sameFile(x, f)))} className="p-1 text-stone hover:text-ink"><X size={13} /></button>
              </li>
            ))}
          </ul>
        )}
        <div className="flex items-end gap-2 px-2 py-2 sm:px-3">
          <FileButton accept={ACCEPT_RECORDS} multiple size="icon" variant="ghost" label="Add files" title="Add spreadsheets or PDF statements" disabled={busy} onFiles={addFiles}>
            <Plus aria-hidden="true" size={20} />
          </FileButton>
          <label htmlFor="chat-input" className="sr-only">What should Leveri work on?</label>
          <textarea
            id="chat-input" ref={area} rows={1} value={text} disabled={busy}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) { e.preventDefault(); void send(); } }}
            placeholder={placeholder ?? "What can Leveri work on?"}
            className="block max-h-[40vh] min-h-[44px] flex-1 resize-none border-0 bg-transparent px-1 py-[10px] text-[17px] leading-[1.45] text-ink placeholder:text-stone focus-visible:outline-none [field-sizing:content]"
            data-testid="chat-input"
          />
          {dictation.available && (
            <button type="button" onClick={dictation.toggle} disabled={busy} aria-pressed={dictation.listening} title={DICTATION_PRIVACY}
              aria-label={dictation.listening ? "Stop dictating" : "Speak the work"}
              className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-control ${dictation.listening ? "bg-forest-tint text-forest" : "text-stone hover:bg-band hover:text-ink"}`}>
              <Mic size={19} />
            </button>
          )}
          <button type="submit" disabled={busy || (!text.trim() && files.length === 0)} aria-label="Send"
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-forest text-forest-on transition-colors hover:bg-forest-hover disabled:bg-brand-line" data-testid="chat-send">
            <ArrowUp size={20} />
          </button>
        </div>
        {dictation.listening && (
          <p role="status" className="flex items-start gap-2 px-4 pb-3 text-[12px] text-ink-soft">
            <Marker tone="progress" className="mt-[5px]" />
            <span><span className="font-medium text-ink">Listening.</span> {DICTATION_PRIVACY}{dictation.heard && <span className="block italic text-stone">{dictation.heard}</span>}</span>
          </p>
        )}
        {dictation.problem && <p className="px-4 pb-3 text-[12px] text-att-text">{dictation.problem}</p>}
      </form>
      {!turn && preview && <p className="-mt-3 px-1 text-[13px] text-stone" data-testid="chat-preview">{preview}</p>}
    </div>
  );
}

const VERDICTS = [
  { value: "WRONG", label: "Wrong" }, { value: "MISSING", label: "Something missing" },
  { value: "NOISE", label: "Not helpful" }, { value: "WORDING", label: "Badly put" },
] as const;

/** "Not right?" — the person's own verdict on Leveri's reply, recorded against the turn so it teaches Leveri (0037). */
function NotRight({ turnKey }: { turnKey: string }) {
  const [open, setOpen] = useState(false);
  const [chosen, setChosen] = useState<(typeof VERDICTS)[number]["value"] | null>(null);
  const [note, setNote] = useState("");
  const [done, setDone] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  async function send(event: FormEvent) {
    event.preventDefault();
    if (!chosen) return;
    try {
      const r = await call("POST", `/api/chat/turns/${turnKey}/feedback`, ChatFeedbackResponse, { correctionClass: chosen, ...(note.trim() ? { note: note.trim() } : {}) });
      setDone(r.sentence);
    } catch (error) { setProblem(failureMessage(error)); }
  }
  if (done) return <p className="text-[12px] text-stone" data-testid="chat-feedback-done">{done}</p>;
  if (!open) return <button type="button" onClick={() => setOpen(true)} className="text-[12px] text-stone hover:text-ink hover:underline" data-testid="chat-not-right">Not right?</button>;
  return (
    <form onSubmit={(e) => void send(e)} className="space-y-2" data-testid="chat-feedback">
      <div className="flex flex-wrap gap-1.5">
        {VERDICTS.map((v) => (
          <button key={v.value} type="button" aria-pressed={chosen === v.value} onClick={() => setChosen(v.value)}
            className={`border px-2.5 py-1 text-[12px] ${chosen === v.value ? "border-ink text-ink" : "border-rule text-ink-soft hover:border-ink"}`}>{v.label}</button>
        ))}
      </div>
      <input value={note} onChange={(e) => setNote(e.target.value)} maxLength={1000} placeholder="What should Leveri have done? (optional)" aria-label="What should Leveri have done?"
        className="h-9 w-full border border-rule bg-transparent px-2.5 text-[13px] text-ink placeholder:text-stone focus-visible:outline-none focus-visible:border-ink" />
      <div className="flex items-center gap-3">
        <button type="submit" disabled={!chosen} className="text-[12px] font-medium text-forest hover:underline disabled:text-stone">Send</button>
        <button type="button" onClick={() => setOpen(false)} className="text-[12px] text-stone hover:text-ink">Cancel</button>
      </div>
      {problem && <p className="text-[12px] text-att-text">{problem}</p>}
    </form>
  );
}

function You({ turn }: { turn: Turn }) {
  return (
    <div className="ml-auto flex max-w-[85%] flex-col items-end gap-2">
      {turn.instruction && <div className="bg-band px-4 py-3 text-[15px] leading-relaxed text-ink" data-testid="chat-you">{turn.instruction}</div>}
      {turn.files.length > 0 && (
        <div className="flex flex-wrap justify-end gap-1.5">
          {turn.files.map((f) => <span key={`${f.name}-${f.size}`} className="border border-rule-soft bg-canvas px-2 py-0.5 font-mono text-[12px] text-ink-soft">{f.name}</span>)}
        </div>
      )}
    </div>
  );
}

function LeveriMark() {
  return <div aria-hidden="true" className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center bg-forest font-serif text-[14px] text-forest-on">L</div>;
}

/** What Leveri is doing, step by step — real acts, in order. The last one in progress is marked. */
function Working({ steps }: { steps: Step[] }) {
  const [openList, setOpen] = useState(true);
  const doing = steps.some((s) => s.state === "doing");
  return (
    <div data-testid="chat-working">
      <button type="button" onClick={() => setOpen((o) => !o)} className="inline-flex items-center gap-2 text-[14px] font-medium text-ink">
        <Marker tone={doing ? "progress" : steps.some((s) => s.state === "failed") ? "attention" : "established"} className={doing ? "animate-pulse" : ""} />
        {doing ? "Working" : "Done"}
        <span className="text-stone">{openList ? "▾" : "▸"}</span>
      </button>
      {openList && (
        <ol className="mt-2 space-y-1.5 border-l border-rule-soft pl-4">
          {steps.map((s, i) => (
            <li key={i} className={`text-[13px] ${s.state === "failed" ? "text-att-text" : s.state === "doing" ? "text-ink" : "text-ink-soft"}`}>{s.text}{s.state === "doing" ? "…" : ""}</li>
          ))}
        </ol>
      )}
    </div>
  );
}

/** One question, with numbered one-tap answers, and the person's own words where they are allowed. */
function QuestionCard({ q, onAnswer }: { q: ChatQuestion; onAnswer: (value: string, label: string) => void }) {
  const [own, setOwn] = useState("");
  const [problem, setProblem] = useState<string | null>(null);
  function useOwn(event: FormEvent) {
    event.preventDefault();
    const words = own.trim();
    if (!words) return;
    if (q.key === "client") return onAnswer(`new:${words}`, `${words} (a new client)`);
    if (q.key === "period") {
      const p = periodFromWords(words, todayIso());
      if (!p) { setProblem("Leveri could not read a period from that. Try “March 2026”, “Q1 2026” or “year ended 31 March 2026”."); return; }
      return onAnswer(JSON.stringify(p), p.label);
    }
    if (q.key === "currency") {
      const code = words.toUpperCase();
      if (!/^[A-Z]{3}$/.test(code)) { setProblem("Use the three-letter code."); return; }
      return onAnswer(code, code);
    }
  }
  return (
    <div className="border border-rule bg-raised" data-testid={`chat-question-${q.key}`}>
      <p className="px-5 pb-2 pt-4 text-[15px] font-medium text-ink">{q.question}</p>
      <ol>
        {q.options.map((o, i) => (
          <li key={o.value}>
            <button type="button" onClick={() => onAnswer(o.value, o.label)} data-testid="chat-option"
              className="flex w-full items-center gap-4 border-t border-rule-soft px-5 py-3 text-left transition-colors hover:bg-band">
              <span className="flex h-6 w-6 shrink-0 items-center justify-center border border-rule font-mono text-[12px] text-stone">{i + 1}</span>
              <span className="min-w-0 flex-1 text-[15px] text-ink">{o.label}</span>
              {o.detail && <span className="text-[12px] text-stone">{o.detail}</span>}
            </button>
          </li>
        ))}
      </ol>
      {q.freeText && (
        <form onSubmit={useOwn} className="flex items-center gap-3 border-t border-rule-soft px-5 py-3">
          <span className="flex h-6 w-6 shrink-0 items-center justify-center border border-dashed border-rule-dashed font-mono text-[12px] text-stone">{q.options.length + 1}</span>
          <input value={own} onChange={(e) => { setOwn(e.target.value); setProblem(null); }} placeholder={q.freeText} aria-label={q.freeText}
            className="h-9 min-w-0 flex-1 border-0 bg-transparent text-[15px] text-ink placeholder:text-stone focus-visible:outline-none" />
          {own.trim() && <button type="submit" className="text-[13px] font-medium text-forest hover:underline">Use this</button>}
        </form>
      )}
      {problem && <p className="px-5 pb-3 text-[13px] text-att-text">{problem}</p>}
    </div>
  );
}

/**
 * THE PLAN CARD — "Here's what I'll do". Everything on it is the server's:
 * the skill's own procedure (`file.procedure`), the records admitted, the
 * start line Leveri proposes, the basis the engagement file settles or the
 * choice it offers, and what the skill will not conclude. One act starts it.
 */
function PlanCard({ plan, basis, onBasis, busy, onApprove, onOpen }: {
  plan: PlanState; basis: "ACCRUALS" | "CASH" | null; onBasis: (b: "ACCRUALS" | "CASH" | null) => void;
  busy: boolean; onApprove: () => void; onOpen: () => void;
}) {
  const { work, file, pick } = plan;
  const admitted = file.records.filter((r) => r.outcome === "ADMITTED");
  const procedure = file.procedure;
  return (
    <div className="border border-rule bg-raised" data-testid="chat-plan">
      <div className="border-b border-rule-soft px-5 pb-3 pt-4">
        <p className="text-[13px] text-stone">Here&rsquo;s what I&rsquo;ll do</p>
        <p className="mt-1 text-[15px] font-medium text-ink">{work.title}</p>
        <p className="text-[12px] text-stone">{work.clientName} · {work.periodText}</p>
      </div>
      <div className="space-y-4 px-5 py-4">
        {procedure && <p className="text-[14px] leading-relaxed text-ink">{procedure.summary}</p>}
        {admitted.length > 0 && (
          <div>
            <p className="text-[12px] font-medium uppercase tracking-wide text-stone">Records</p>
            <ul className="mt-1 space-y-0.5">
              {admitted.map((r) => <li key={r.evidenceReferenceId ?? r.filename} className="text-[13px] text-ink-soft">{r.filename}{r.roleLabel ? ` — ${r.roleLabel}` : ""}</li>)}
            </ul>
          </div>
        )}
        {procedure && (
          <ol className="space-y-1.5">
            {procedure.steps.map((step, i) => (
              <li key={step} className="grid grid-cols-[22px_minmax(0,1fr)] gap-2 text-[13px] text-ink">
                <span className="text-stone [font-variant-numeric:tabular-nums]">{i + 1}</span><span>{step}</span>
              </li>
            ))}
          </ol>
        )}
        {pick && <p className="text-[13px] text-ink-soft">{file.start.chooser.heading}: <span className="text-ink">{pick.label}</span></p>}
        {file.start.basisSettled && <p className="text-[13px] text-ink-soft">{file.start.basisSettled}</p>}
        {!file.start.basisSettled && file.start.basisChoice && pick && (
          <div>
            <p className="text-[13px] text-ink-soft">{file.start.basisChoice.question}</p>
            <div className="mt-1.5 flex flex-wrap gap-2">
              {file.start.basisChoice.options.map((o) => (
                <button key={o.value} type="button" onClick={() => onBasis(basis === o.value ? null : o.value)} aria-pressed={basis === o.value}
                  className={`border px-3 py-1.5 text-[13px] ${basis === o.value ? "border-forest text-forest" : "border-rule text-ink-soft hover:bg-band"}`}>{o.label}</button>
              ))}
            </div>
          </div>
        )}
        {!pick && file.start.blockers.length > 0 && (
          <div>
            <p className="text-[12px] font-medium uppercase tracking-wide text-stone">Before it can start</p>
            <ul className="mt-1 space-y-0.5">{file.start.blockers.map((b) => <li key={b} className="text-[13px] text-att-text">{b}</li>)}</ul>
          </div>
        )}
        {procedure && procedure.boundaries.length > 0 && (
          <details>
            <summary className="cursor-pointer text-[13px] text-stone">What it will not conclude</summary>
            <ul className="mt-1.5 space-y-1">{procedure.boundaries.map((b) => <li key={b} className="text-[13px] text-ink-soft">{b}</li>)}</ul>
          </details>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-3 border-t border-rule-soft px-5 py-3">
        {pick && (
          <button type="button" onClick={onApprove} disabled={busy} data-testid="chat-approve"
            className="bg-forest px-4 py-2 text-[14px] font-medium text-forest-on hover:bg-forest-hover disabled:opacity-60">Approve and start</button>
        )}
        <button type="button" onClick={onOpen} className="text-[14px] font-medium text-forest hover:underline">{pick ? "Open the work instead" : "Open the work"}</button>
      </div>
    </div>
  );
}
